import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import ClaimDialog from "./claimDialog";
import { useClaimMotion } from "./claimMotion";

const REJECTION_REASONS = [
  { key: "Incorrect Offer", en: "Incorrect offer", ar: "العرض غير صحيح" },
  { key: "Required Repairs", en: "Required repairs", ar: "إصلاحات مطلوبة" },
];

/**
 * Accepting or rejecting a settlement offer.
 *
 * Same two steps and the same request as before, rebuilt. What changed:
 *
 *  - It is one dialog with two steps instead of two overlapping fixed divs,
 *    one rendered inside the other and both hidden with a `hidden` class.
 *  - The counter offer is validated. Submitting the old form empty sent
 *    `parseFloat("")` — NaN — straight to the API as the customer's counter.
 *  - Both languages. The whole flow, including "Reason of Rejection" and the
 *    two reason buttons, was English-only on an Arabic page.
 *  - There is a way back. Once you pressed Reject there was no route to Accept
 *    short of closing and reopening.
 */
const OfferResponseDialog = ({
  open,
  onClose,
  offer,
  onRespond,
  isSubmitting,
}) => {
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const [step, setStep] = useState("decide");
  const [reason, setReason] = useState(REJECTION_REASONS[0].key);
  const [counterOffer, setCounterOffer] = useState("");

  // A dialog reopened for a different offer must not inherit the last one's
  // half-filled rejection.
  useEffect(() => {
    if (open) {
      setStep("decide");
      setReason(REJECTION_REASONS[0].key);
      setCounterOffer("");
    }
  }, [open, offer?.id]);

  const counterValue = Number.parseFloat(counterOffer);
  const counterIsValid = Number.isFinite(counterValue) && counterValue > 0;

  const submitLabel = translate("Submit response", "إرسال الرد");

  return (
    <ClaimDialog
      open={open}
      onClose={onClose}
      title={
        step === "decide"
          ? translate("Settlement offer", "عرض التسوية")
          : translate("Reason for rejection", "سبب الرفض")
      }
      description={
        step === "decide"
          ? translate(
              "Review the offer below and let us know how you would like to proceed.",
              "راجع العرض أدناه وأخبرنا كيف ترغب في المتابعة."
            )
          : translate(
              "Tell us why you are rejecting this offer, and what you expected instead.",
              "أخبرنا سبب رفضك لهذا العرض، والمبلغ الذي تتوقعه بدلاً منه."
            )
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {step === "decide" ? (
          <motion.div key="decide" {...motionPresets.panel(step === "reject" ? 1 : -1)}>
            <div className="rounded-xl border border-[#DDE2E4] bg-[#F6F8F9] p-4 text-center">
              {offer?.status && (
                <p className="text-sm leading-5 text-[#5B6871]">
                  {offer.status}
                </p>
              )}
              {/* The number is the decision, so it gets the size. Tabular
                  figures keep it from shifting as the value changes. */}
              <p className="mt-1 text-2xl font-semibold tracking-[-0.24px] text-[#1A2024] [font-variant-numeric:tabular-nums]">
                {offer?.amount} {translate("BHD", "د.ب")}
              </p>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setStep("reject")}
                className="order-2 flex-1 rounded-xl border border-[#DDE2E4] bg-white px-4 py-3 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#F0F3F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] disabled:opacity-60 sm:order-1"
              >
                {translate("Reject", "رفض")}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => onRespond({ decision: "Approved", desc: "" })}
                className="order-1 flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#55C6EF] px-4 py-3 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#3FBCEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2 disabled:opacity-60 sm:order-2"
              >
                {isSubmitting && (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                )}
                {translate("Accept offer", "قبول العرض")}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="reject" {...motionPresets.panel(step === "reject" ? 1 : -1)}>
            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-[#1A2024]">
                {translate("Reason", "السبب")}
              </legend>
              <div className="flex gap-2">
                {REJECTION_REASONS.map((option) => {
                  const selected = reason === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setReason(option.key)}
                      className={cn(
                        "flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7]",
                        selected
                          ? "border-[#00A9E7] bg-[#E4F5FD] text-[#00668F]"
                          : "border-[#DDE2E4] bg-white text-[#48535B] hover:bg-[#F6F8F9]"
                      )}
                    >
                      {translate(option.en, option.ar)}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="mt-5">
              <label
                htmlFor="claim-counter-offer"
                className="mb-2 block text-sm font-semibold text-[#1A2024]"
              >
                {translate("Your counter offer", "عرضك المقابل")}
              </label>
              <div className="relative">
                <input
                  id="claim-counter-offer"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.001"
                  value={counterOffer}
                  onChange={(event) => setCounterOffer(event.target.value)}
                  placeholder="0.000"
                  className="w-full rounded-xl border border-[#DDE2E4] py-3 pe-16 ps-4 text-lg font-medium text-[#1A2024] [font-variant-numeric:tabular-nums] focus:border-[#00A9E7] focus:outline-none focus:ring-2 focus:ring-[#00A9E7]/30"
                />
                <span className="pointer-events-none absolute inset-y-0 end-4 flex items-center text-sm font-semibold text-[#5B6871]">
                  {translate("BHD", "د.ب")}
                </span>
              </div>
              <p className="mt-2 text-xs leading-4 text-[#6E7C87]">
                {translate(
                  "Enter the amount you believe this claim should settle at.",
                  "أدخل المبلغ الذي تعتقد أنه يجب تسوية المطالبة به."
                )}
              </p>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setStep("decide")}
                className="order-2 flex items-center justify-center gap-1.5 rounded-xl border border-[#DDE2E4] bg-white px-4 py-3 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#F0F3F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] disabled:opacity-60 sm:order-1"
              >
                <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
                {translate("Back", "رجوع")}
              </button>
              <button
                type="button"
                disabled={isSubmitting || !counterIsValid}
                onClick={() =>
                  onRespond({
                    decision: "Rejected",
                    desc: reason,
                    amount: counterValue,
                  })
                }
                className="order-1 flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#55C6EF] px-4 py-3 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#3FBCEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:order-2"
              >
                {isSubmitting && (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                )}
                {submitLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ClaimDialog>
  );
};

export default OfferResponseDialog;
